/**
 * Fullscreen + keyboard lock. Ctrl+W closes the tab and no `preventDefault`
 * stops it; the ONE sanctioned way round is the Keyboard Lock API, available to
 * a page in element-fullscreen on Chromium (Escape must be HELD to leave).
 * Firefox and Safari get a plain fullscreen toggle; `main.ts`'s `beforeunload`
 * guard catches the stray Ctrl+W. F11 does not count — it fires no
 * `fullscreenchange` and grants no lock — so it is F10 and a settings button.
 */

interface KeyboardLock {
  lock(codes?: string[]): Promise<void>;
  unlock(): void;
}

/** The panel is also rendered headless in tests, where there is no DOM. */
const HAS_DOM = typeof document !== "undefined";

function keyboardLock(): KeyboardLock | null {
  if (typeof navigator === "undefined") return null;
  const kb = (navigator as { keyboard?: Partial<KeyboardLock> }).keyboard;
  return kb !== undefined && typeof kb.lock === "function" && typeof kb.unlock === "function"
    ? (kb as KeyboardLock)
    : null;
}

/** True when the browser can hand the reserved chords to the page. */
export const KEYBOARD_LOCK_SUPPORTED = keyboardLock() !== null;

export function isFullscreen(): boolean {
  return HAS_DOM && document.fullscreenElement !== null;
}

/**
 * Enters or leaves fullscreen. Must run from a user gesture. The promise is
 * swallowed — a refusal is nothing the player can act on.
 */
export function toggleFullscreen(): void {
  if (isFullscreen()) {
    void document.exitFullscreen().catch(() => undefined);
    return;
  }
  void document.documentElement.requestFullscreen({ navigationUI: "hide" }).catch(() => undefined);
}

/**
 * Keeps the keyboard lock in step with fullscreen. Locks EVERYTHING rather than
 * just Ctrl+W — a player in fullscreen wants Tab and the F-keys to be the
 * game's. Returns a disposer.
 */
export function installKeyboardLock(): () => void {
  const kb = keyboardLock();
  if (kb === null) return () => undefined;
  const sync = (): void => {
    if (isFullscreen()) void kb.lock().catch(() => undefined);
    else kb.unlock();
  };
  document.addEventListener("fullscreenchange", sync);
  sync();
  return () => {
    document.removeEventListener("fullscreenchange", sync);
    kb.unlock();
  };
}
